import React, { useCallback, useEffect, useState } from "react";
import { useDashboardCarouselSwipe } from "../../hooks/useDashboardCarouselSwipe";
import { useOrganization } from "../../context/OrganizationContext";
import { usePermissions } from "../../hooks/usePermissions";
import {
  loadAccountsPayableOpsSummary,
  loadAccountsReceivableOpsSummary,
  loadClientsOpsSummary,
  loadEstimatePipelineOpsSummary,
  loadFinancialPositionOpsSummary,
  loadProjectsOpsSummary,
} from "../../helpers/opsDashboardSummaries";
import { FINANCE_POSITION_CHANGED_EVENT } from "../../helpers/financeStateEvents";
import { SnapshotMetricCard } from "./SnapshotMetricCard";

const MOBILE_PAGE_STYLE = {
  minHeight: "100vh",
  padding: "24px 18px 90px",
  color: "var(--nx-text, #1a2233)",
  background: "var(--nx-bg, #f4f5f7)",
};

/**
 * Clickable ops summary card — same chrome as legacy SnapshotMetricCard.
 * Entire card opens the destination module.
 * `accent` restores module color identity (title / border / wash only).
 */
export function OpsSummaryCard({
  title,
  subtitle = "Tap to open",
  metrics = [],
  loading = false,
  error = null,
  onOpen,
  emptyMessage = "No data yet.",
  accent = null,
}) {
  const rows = loading
    ? [{ key: "loading", label: "Loading…", value: "—" }]
    : error
      ? [{ key: "error", label: "Unable to load", value: "—" }]
      : metrics;

  return (
    <button
      type="button"
      className="ops-summary-card-button"
      data-accent={accent || undefined}
      onClick={onOpen}
      disabled={typeof onOpen !== "function"}
      style={{
        display: "block",
        width: "100%",
        padding: 0,
        margin: 0,
        border: "none",
        background: "transparent",
        color: "inherit",
        font: "inherit",
        textAlign: "left",
        cursor: typeof onOpen === "function" ? "pointer" : "default",
      }}
      aria-label={title ? `Open ${title}` : "Open module"}
    >
      <SnapshotMetricCard
        title={title}
        subtitle={subtitle}
        metrics={rows}
        emptyMessage={emptyMessage}
      />
    </button>
  );
}

function useOpsSummaryLoader(loader, deps, { refreshEvent = null } = {}) {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await loader();
      setMetrics(next || []);
      setError(null);
    } catch (err) {
      console.error("[OpsSummary] load failed:", err);
      setError(err);
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!refreshEvent || typeof window === "undefined") {
      return undefined;
    }
    const onRefresh = () => {
      void load();
    };
    window.addEventListener(refreshEvent, onRefresh);
    return () => window.removeEventListener(refreshEvent, onRefresh);
  }, [load, refreshEvent]);

  return { metrics, loading, error };
}

export function AccountsReceivableOpsSummary({ currentUser, onOpen }) {
  const { metrics, loading, error } = useOpsSummaryLoader(
    () => loadAccountsReceivableOpsSummary(currentUser),
    [currentUser],
    { refreshEvent: FINANCE_POSITION_CHANGED_EVENT },
  );
  return (
    <OpsSummaryCard
      title="Accounts Receivable"
      accent="receivable"
      metrics={metrics}
      loading={loading}
      error={error}
      onOpen={onOpen}
    />
  );
}

export function AccountsPayableOpsSummary({ currentUser, onOpen }) {
  const { settings } = useOrganization();
  const { metrics, loading, error } = useOpsSummaryLoader(
    () => loadAccountsPayableOpsSummary(currentUser, settings),
    [currentUser, settings],
  );
  return (
    <OpsSummaryCard
      title="Accounts Payable"
      accent="payable"
      metrics={metrics}
      loading={loading}
      error={error}
      onOpen={onOpen}
    />
  );
}

export function FinancialPositionOpsSummary({ currentUser, onOpen }) {
  const { metrics, loading, error } = useOpsSummaryLoader(
    () => loadFinancialPositionOpsSummary(currentUser),
    [currentUser],
    { refreshEvent: FINANCE_POSITION_CHANGED_EVENT },
  );
  return (
    <OpsSummaryCard
      title="Financial Position"
      accent="finance"
      metrics={metrics}
      loading={loading}
      error={error}
      onOpen={onOpen}
    />
  );
}

export function EstimatePipelineOpsSummary({ currentUser, onOpen }) {
  const { actor } = usePermissions();
  const { metrics, loading, error } = useOpsSummaryLoader(
    () => loadEstimatePipelineOpsSummary(actor || currentUser),
    [actor, currentUser],
  );
  return (
    <OpsSummaryCard
      title="Estimate Pipeline"
      accent="estimates"
      metrics={metrics}
      loading={loading}
      error={error}
      onOpen={onOpen}
    />
  );
}

export function ClientsOpsSummary({ currentUser, onOpen }) {
  const { actor } = usePermissions();
  const { metrics, loading, error } = useOpsSummaryLoader(
    () => loadClientsOpsSummary(actor || currentUser),
    [actor, currentUser],
  );
  return (
    <OpsSummaryCard
      title="Clients"
      accent="clients"
      metrics={metrics}
      loading={loading}
      error={error}
      onOpen={onOpen}
    />
  );
}

export function ProjectsOpsSummary({ currentUser, onOpen }) {
  const { actor, directReportIds, orgPolicies } = usePermissions();
  const { metrics, loading, error } = useOpsSummaryLoader(
    () =>
      loadProjectsOpsSummary(actor || currentUser, {
        directReportIds,
        orgPolicies,
      }),
    [actor, currentUser, directReportIds, orgPolicies],
  );
  return (
    <OpsSummaryCard
      title="Projects"
      accent="projects"
      metrics={metrics}
      loading={loading}
      error={error}
      onOpen={onOpen}
    />
  );
}

/**
 * Mobile carousel page: one ops summary + existing swipe navigation.
 */
export function OpsDashboardCarouselPage({
  children,
  onCarouselNavigate,
  onOpenNavigator,
}) {
  const { ref, style } = useDashboardCarouselSwipe({
    onNavigate: onCarouselNavigate,
    onOpenNavigator,
  });

  return (
    <div ref={ref} style={{ ...MOBILE_PAGE_STYLE, ...style }}>
      {children}
    </div>
  );
}

export function renderOpsDashboardSummary(screenId, {
  currentUser,
  onOpenAccountsReceivable,
  onOpenAccountsPayable,
  onOpenFinance,
  onOpenEstimateHub,
  onOpenClients,
  onOpenProjects,
}) {
  switch (screenId) {
    case "accounts_receivable":
      return (
        <AccountsReceivableOpsSummary
          currentUser={currentUser}
          onOpen={onOpenAccountsReceivable}
        />
      );
    case "accounts_payable":
      return (
        <AccountsPayableOpsSummary
          currentUser={currentUser}
          onOpen={onOpenAccountsPayable}
        />
      );
    case "financial_position":
      return (
        <FinancialPositionOpsSummary
          currentUser={currentUser}
          onOpen={onOpenFinance}
        />
      );
    case "estimate_pipeline":
      return (
        <EstimatePipelineOpsSummary
          currentUser={currentUser}
          onOpen={onOpenEstimateHub}
        />
      );
    case "create_client":
      return (
        <ClientsOpsSummary currentUser={currentUser} onOpen={onOpenClients} />
      );
    case "create_project":
      return (
        <ProjectsOpsSummary
          currentUser={currentUser}
          onOpen={onOpenProjects}
        />
      );
    default:
      return null;
  }
}
